import React from 'react';
import { EntityAPIResponse } from "src/@types/SNOWDataServiceTypes";
import { formatDate } from "src/utils/CommonUtils";
import { getWorkflowFiltersByName } from "src/constants/WorkflowDetails";
import { getEntityProperties } from "src/utils/AttributeUtils";
import { Link } from '@amzn/awsui-components-console';
import { UserIcon } from 'src/components/commonComponents/UserIcon';

import {
  DASHBOARD_COLUMNS,
  VISIBLE_COLUMNS,
} from "./entitySearchConstants";

const VALUE_LIST_ATTRIBUTES = ['old_node_properties', 'node_properties'];
const DATE_ATTRIBUTES = ['needByDate'];
const USER_ICON_ATTRIBUTES = ['Approver'];

const renderUserIcon = (user: string) => {
  return React.createElement(UserIcon as React.ComponentType<{ user: string }>, { user: user || '' });
};

export const getTableDetails = (
  entityResponse: EntityAPIResponse<object>,
  selectedWorkflowType
) => {
	const workflowSpecificFilterSchema = getWorkflowFiltersByName(selectedWorkflowType);
	
  var entityItems = (entityResponse?.entities ?? [])
    .filter((entityData) => entityData.EntityName && entityData.EntityName.trim() !== '')
    .map((entityData) => {
    var entityItem = {
      node_id: entityData.EntityName,
      workflow_id: entityData.WorkflowType,
      workflow_type: entityData.WorkflowType,
      entity_type: entityData.EntityType,
      region: entityData.Region,
      country: entityData.Country,
      created_by: (entityData.EntityAttributes as any)?.submitted_by || entityData.CreatedBy,
      creation_time: formatDate(entityData.CreationTime),
      workflow_status: entityData.Status,
      cycle_id: entityData.CycleId,
      group_id: workflowSpecificFilterSchema.groupIdAttributeId ? entityData.EntityData[workflowSpecificFilterSchema.groupIdAttributeId!] : null
    };

    if (workflowSpecificFilterSchema.attributeList) {
      workflowSpecificFilterSchema.attributeList!.forEach((attribute) => {
        entityItem[attribute.id] = entityData.EntityData[attribute.id] || (entityData.EntityAttributes as any)?.[attribute.id];
      });
    }

    return entityItem;
  });

  var newTableColumns: any[] = [...DASHBOARD_COLUMNS];

  if (selectedWorkflowType) {
    newTableColumns = newTableColumns.filter(col => col.id !== 'workflow_type');
  }

  if (workflowSpecificFilterSchema.attributeList) {
    const columns = workflowSpecificFilterSchema.attributeList!.map(
      (attribute) => ({
        id: attribute.id,
        header: attribute.name,
        cell: (item) => {
          const value = item[attribute.id];
          if (USER_ICON_ATTRIBUTES.includes(attribute.id)) {
            return renderUserIcon(value);
          }
          if (VALUE_LIST_ATTRIBUTES.includes(attribute.id)) {
            return getEntityProperties(value);
          }
          if (DATE_ATTRIBUTES.includes(attribute.id)) {
            return formatDate(value);
          }
          return value;
        },
        width: 200,
        minWidth: 150,
      })
    );
    newTableColumns.splice(2, 0, ...columns);
  }

  if (workflowSpecificFilterSchema.actionColumn) {
    const actionColumnIndex = 3 + (workflowSpecificFilterSchema.attributeList?.length || 0);
    newTableColumns.splice(actionColumnIndex, 0, {
      id: 'actions',
      header: 'Actions',
      cell: (item) => {
        const { conditionalStatus, conditions } = workflowSpecificFilterSchema.actionColumn!;

        if (conditions && conditions.length > 0) {
          const allMatch = conditions.every(({ field, value }) => item[field] === value);
          if (!allMatch) return '';
        } else if (conditionalStatus && item.workflow_status !== conditionalStatus) {
          // Backwards-compatible single status check
          return '';
        }

        const url = workflowSpecificFilterSchema.actionColumn!.urlTemplate.replace(/{(\w+)}/g, (_, key) => item[key]);
        return React.createElement(Link, { href: url, target: '_blank' }, workflowSpecificFilterSchema.actionColumn!.label);
      },
      width: 120,
      minWidth: 120,
    });
  }

  if (workflowSpecificFilterSchema.groupIdAttributeId) {
    newTableColumns.splice(3, 0, {
      id: "group_id",
      header: workflowSpecificFilterSchema.groupIdAttributeName!,
      cell: (item) => item.group_id,
      width: 120,
      minWidth: 120,
    });
  }

  const newVisibleContent = newTableColumns
    .filter(col => VISIBLE_COLUMNS.includes(col.id) || !DASHBOARD_COLUMNS.find(dc => dc.id === col.id))
    .map(col => col.id);

  return { entityItems, newTableColumns, newVisibleContent };
};

